/** Einfaches Tabellen-PDF (Helvetica, WinAnsi) ohne zusätzliche Bibliothek. */

const EXTRA = {
  0x20ac: 0x80,
  0x201a: 0x82,
  0x201e: 0x84,
  0x2026: 0x85,
  0x2020: 0x86,
  0x2021: 0x87,
  0x2030: 0x89,
  0x2018: 0x91,
  0x2019: 0x92,
  0x201c: 0x93,
  0x201d: 0x94,
  0x2022: 0x95,
  0x2013: 0x96,
  0x2014: 0x97,
  0x2122: 0x99,
};

function winAnsi(text) {
  const bytes = [];
  for (const ch of String(text ?? '')) {
    const cp = ch.codePointAt(0);
    if (cp < 128) bytes.push(cp);
    else if (cp >= 0xa0 && cp <= 0xff) bytes.push(cp);
    else if (EXTRA[cp]) bytes.push(EXTRA[cp]);
    else bytes.push(0x3f);
  }
  return bytes;
}

function pdfString(bytes) {
  const out = [0x28];
  bytes.forEach((b) => {
    if (b === 0x28 || b === 0x29 || b === 0x5c) out.push(0x5c);
    out.push(b);
  });
  out.push(0x29);
  return out;
}

function latin1(text) {
  const out = new Uint8Array(text.length);
  for (let i = 0; i < text.length; i += 1) out[i] = text.charCodeAt(i) & 0xff;
  return out;
}

function concat(parts) {
  const len = parts.reduce((sum, part) => sum + part.length, 0);
  const out = new Uint8Array(len);
  let offset = 0;
  parts.forEach((part) => {
    out.set(part, offset);
    offset += part.length;
  });
  return out;
}

function clip(text, max) {
  const value = String(text ?? '');
  if (value.length <= max) return value;
  return `${value.slice(0, Math.max(0, max - 1))}…`;
}

function pageStream(title, headers, rows, pageNo, pageCount) {
  const chunks = [];
  const textAt = (x, y, size, value) => {
    chunks.push(latin1(`BT /F1 ${size} Tf 1 0 0 1 ${x} ${y} Tm `));
    chunks.push(pdfString(winAnsi(value)));
    chunks.push(latin1(' Tj ET\n'));
  };
  textAt(36, 560, 14, title);
  textAt(700, 560, 9, `Seite ${pageNo} / ${pageCount}`);
  const colW = Math.floor(770 / Math.max(headers.length, 1));
  headers.forEach((header, index) => {
    textAt(36 + index * colW, 534, 9, clip(header, Math.floor(colW / 5)));
  });
  rows.forEach((row, rowIndex) => {
    const y = 516 - rowIndex * 14;
    row.forEach((cell, index) => {
      textAt(36 + index * colW, y, 9, clip(cell, Math.floor(colW / 5)));
    });
  });
  return concat(chunks);
}

function paginate(rows) {
  const size = 32;
  if (!rows.length) return [[]];
  const pages = [];
  for (let i = 0; i < rows.length; i += size) pages.push(rows.slice(i, i + size));
  return pages;
}

export function buildListPdf({ title, headers, rows }) {
  const pages = paginate(rows || []);
  const streams = pages.map((pageRows, index) => (
    pageStream(title || 'Liste', headers || [], pageRows, index + 1, pages.length)
  ));
  const objects = [
    latin1('<< /Type /Catalog /Pages 2 0 R >>'),
    null,
    latin1('<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica /Encoding /WinAnsiEncoding >>'),
  ];
  const pageIds = [];
  streams.forEach((stream) => {
    const pageId = objects.length + 1;
    const contentId = pageId + 1;
    pageIds.push(pageId);
    objects.push(latin1(
      `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 842 595] /Contents ${contentId} 0 R /Resources << /Font << /F1 3 0 R >> >> >>`,
    ));
    const body = concat([
      latin1(`<< /Length ${stream.length} >>\nstream\n`),
      stream,
      latin1('\nendstream'),
    ]);
    objects.push(body);
  });
  objects[1] = latin1(
    `<< /Type /Pages /Kids [${pageIds.map((id) => `${id} 0 R`).join(' ')}] /Count ${pageIds.length} >>`,
  );

  const header = latin1('%PDF-1.4\n');
  const parts = [header];
  const offsets = [0];
  let cursor = header.length;
  objects.forEach((obj, index) => {
    offsets.push(cursor);
    const wrapped = concat([latin1(`${index + 1} 0 obj\n`), obj, latin1('\nendobj\n')]);
    parts.push(wrapped);
    cursor += wrapped.length;
  });
  const xrefAt = cursor;
  let xref = `xref\n0 ${objects.length + 1}\n0000000000 65535 f \n`;
  for (let i = 1; i < offsets.length; i += 1) {
    xref += `${String(offsets[i]).padStart(10, '0')} 00000 n \n`;
  }
  xref += `trailer\n<< /Size ${objects.length + 1} /Root 1 0 R >>\nstartxref\n${xrefAt}\n%%EOF`;
  parts.push(latin1(xref));
  return concat(parts);
}
